import { Button, Heading, List, Stack, Text } from '@chakra-ui/react'
import type { MemberCard } from '@ipc/shared'
import type { ReactNode } from 'react'
import { Link as RouterLink } from 'react-router'
import { ActionBar } from '~/components/member/ActionBar'
import { TextLink } from '~/components/member/layout'
import { Rich, useT } from '~/i18n/use-t'

const REASONS = ['age', 'place', 'face', 'document'] as const

/**
 * The card titles carry the member's name, which can be long: a step smaller on a phone, balanced lines and a long
 * name that breaks rather than overflows (mobile review).
 */
const titleProps = {
  size: { base: 'xl', sm: '2xl', md: '3xl' },
  textWrap: 'balance',
  overflowWrap: 'anywhere',
} as const

/**
 * A stored name no longer carries initials (first name only), but a typed name can still end with a period ("J.
 * Smith"); a title that puts a period after the name would read "J. Smith..". Keep one period.
 */
export function endsOnce(text: string, name: string): string {
  return name.endsWith('.') ? text.split(`${name}.`).join(name) : text
}

/**
 * The status card in plain words (copy v8 S6): welcome, "needs one more look" with the reasons that apply,
 * "please do the face check again", or "not accepted" with its reason in plain words. Never a score.
 */
export function StatusCard({
  card,
  name,
  reasonCategories = [],
  rejectReasonCategory,
  providerKeepsPhotos = false,
  providerName,
  retryTo,
  deleteTo,
  writeToUsTo,
  documentVerified = false,
  children,
}: {
  card: MemberCard
  name?: string | null
  reasonCategories?: readonly string[]
  rejectReasonCategory?: string
  /** `faceNotice === 'kept_for_review'`: the line about the company keeping the photos while a person looks. */
  providerKeepsPhotos?: boolean
  providerName?: string
  retryTo?: string
  deleteTo?: string
  /** Where the "Write to us" section is; the review card links to it. Without it the card shows the contact address. */
  writeToUsTo?: string
  /** The enrollment rests on a checked document: the new face check needs no document (spec §12.1 step 7). */
  documentVerified?: boolean
  /** More actions for the action bar (for example "Go to my member page"), under the card's own button. */
  children?: ReactNode
}) {
  const { t, config } = useT()
  const who = name ? { name } : undefined
  // The card's own button (a retry or a delete link) and the page's extras share one action bar.
  const own =
    card === 'needs_action' && retryTo ? (
      <Button asChild size='lg' w='full'>
        <RouterLink to={retryTo}>{t('status.needsButton')}</RouterLink>
      </Button>
    ) : card === 'not_accepted' && deleteTo ? (
      <Button asChild size='lg' w='full'>
        <RouterLink to={deleteTo}>{t('status.deleteButton')}</RouterLink>
      </Button>
    ) : null
  const bar = (own || children) && (
    <ActionBar>
      {own}
      {children}
    </ActionBar>
  )
  const title = (withName: string, without: string) => (who ? endsOnce(t(withName, who), who.name) : t(without))

  if (card === 'approved')
    return (
      <Stack gap={3}>
        <Heading as='h1' {...titleProps}>
          {title('status.approvedTitle', 'status.approvedTitleNoName')}
        </Heading>
        <Text>{t('status.approvedText')}</Text>
        {bar}
      </Stack>
    )

  if (card === 'review')
    return (
      <Stack gap={4}>
        <Heading as='h1' {...titleProps}>
          {title('status.reviewTitle', 'status.reviewTitleNoName')}
        </Heading>
        <Text>{t('status.reviewNormal')}</Text>
        <Text>{t('status.reviewText')}</Text>
        {reasonCategories.length > 0 && (
          <Stack gap={2}>
            <Text fontStyle='italic' fontWeight='bold'>
              {t('status.why')}
            </Text>
            <List.Root ps={6} gap={2}>
              {REASONS.filter((r) => reasonCategories.includes(r)).map((r) => (
                <List.Item key={r}>
                  <b>{t(`status.reason${r[0]!.toUpperCase()}${r.slice(1)}`)}</b>{' '}
                  {t(`status.reason${r[0]!.toUpperCase()}${r.slice(1)}Text`)}
                </List.Item>
              ))}
            </List.Root>
          </Stack>
        )}
        {providerKeepsPhotos && providerName && (
          <Text fontStyle='italic'>{t('status.keeps', { provider: providerName })}</Text>
        )}
        <Text>
          {writeToUsTo ? (
            <Rich k='status.nothingToDo' components={{ a: <TextLink to={writeToUsTo}>{''}</TextLink> }} />
          ) : (
            t('status.nothingToDoContact')
          )}
        </Text>
        {bar}
      </Stack>
    )

  if (card === 'needs_action')
    return (
      <Stack gap={4}>
        <Heading as='h1' {...titleProps}>
          {title('status.needsTitle', 'status.needsTitleNoName')}
        </Heading>
        <Text>{t('status.needsText')}</Text>
        {documentVerified && <Text>{t('join.document.alreadyVerified')}</Text>}
        {bar}
      </Stack>
    )

  const reasonKey = ['age', 'place', 'face', 'duplicate', 'other'].includes(rejectReasonCategory ?? '')
    ? (rejectReasonCategory as string)
    : 'other'
  return (
    <Stack gap={4}>
      <Heading as='h1' {...titleProps}>
        {t('status.notAcceptedTitle')}
      </Heading>
      <Text>{t('status.notAcceptedText', { reason: t(`status.reasonCat.${reasonKey}`) })}</Text>
      <Text>{t(config.org.phone.trim() ? 'status.notAcceptedHelp' : 'status.notAcceptedHelpNoPhone')}</Text>
      {bar}
    </Stack>
  )
}
